import {
  GatePeek,
  GatePeekPanel,
  buildGatedAriaLabel,
  gatingSurfaceTitle,
  PUBLISH_JOB_FEATURE_KEY,
  PageHeaderActions,
  linkedinShareEnabled,
  CopyPublicLinkButton,
  PublicShareMenu,
  PublicShareMenuItems,
  ViewPublicLinkButton,
  ButtonDropdownChevron,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useGatedAction,
  lifecycleActionNeedsConfirm,
  LIFECYCLE_ACTION_LABELS,
  navigateWithThreadPreservation,
} from '@yeshuman/sdk'
import type { LifecycleAction } from '@yeshuman/sdk'
import {
  Archive,
  CircleOff,
  Copy,
  ExternalLink,
  EyeOff,
  Loader2,
  LockKeyhole,
  MoreHorizontal,
  RotateCcw,
  Send,
  Trash2,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { JobDuplicateButton } from './job-duplicate-button'
import { JobWorkspaceActionDialogs } from './job-workspace-action-dialogs'
import { PublishReadinessPeek } from './publish-readiness-peek'
import { useEmployerJobOrgSlug } from '../../hooks/use-employer-job-org-slug'
import type { OrganisationOpportunity } from '../../hooks/use-organisation-opportunities'
import { useJobWorkspaceActions } from '../../hooks/use-job-workspace-actions'
import type { QueryClient } from '@tanstack/react-query'

function lifecycleIcon(action: LifecycleAction, status: ReturnType<typeof useJobWorkspaceActions>['status']) {
  switch (action) {
    case 'publish':
      return status === 'closed' ? RotateCcw : Send
    case 'unpublish':
      return EyeOff
    case 'close':
      return CircleOff
    case 'archive':
      return Archive
    case 'restore':
      return RotateCcw
    default:
      return Send
  }
}

type JobWorkspaceHeaderActionsProps = {
  opportunity: OrganisationOpportunity
  token: string | null
  queryClient: QueryClient
  jobDetailUrl: string
  onPublished?: (detailUrl: string | null) => void
  onShareLinkedIn?: () => void
  onAfterTransition?: (action: LifecycleAction) => void
  onDeleted?: () => void
  onDuplicated?: (newOpportunityId: string) => void
  onFixPublishGaps?: () => void
}

/** Expanded toolbar on wide panes; single overflow menu when the page container is narrow. */
export function JobWorkspaceHeaderActions({
  opportunity,
  token,
  queryClient,
  jobDetailUrl,
  onPublished,
  onShareLinkedIn,
  onAfterTransition,
  onDeleted,
  onDuplicated,
  onFixPublishGaps,
}: JobWorkspaceHeaderActionsProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [publishGateDialogOpen, setPublishGateDialogOpen] = useState(false)
  const orgSlug = useEmployerJobOrgSlug(opportunity.organisation_slug)
  const publishGate = useGatedAction(orgSlug, 'job_board.publish')

  const actions = useJobWorkspaceActions({
    opportunity,
    token,
    queryClient,
    onPublished,
    onAfterTransition,
    onDeleted,
    onDuplicated,
  })

  const {
    busy,
    status,
    primary,
    dropdown,
    showDelete,
    primaryLabel,
    publishBlocked,
    publishBlockedTitle,
    publishBlockerItems,
    pendingConfirm,
    setPendingConfirm,
    pendingDelete,
    setPendingDelete,
    runAction,
    runDelete,
    runDuplicate,
    handleAction,
  } = actions

  const publishEntitlementBlocked =
    primary === 'publish' &&
    publishGate.plansEnabled &&
    !publishGate.isLoading &&
    !publishGate.allowed &&
    !publishBlocked

  const handlePrimaryClick = useCallback(() => {
    if (publishEntitlementBlocked) {
      setPublishGateDialogOpen(true)
      return
    }
    handleAction(primary!, false)
  }, [handleAction, primary, publishEntitlementBlocked])

  const handlePublishUpgrade = useCallback(() => {
    setPublishGateDialogOpen(false)
    navigateWithThreadPreservation(navigate, publishGate.upgradePath, searchParams)
  }, [navigate, publishGate.upgradePath, searchParams])

  const handleFixPublishGaps = useCallback(() => {
    onFixPublishGaps?.()
  }, [onFixPublishGaps])

  if (!primary) {
    return null
  }

  const primaryVariant =
    status === 'published' ? 'outline' : status === 'archived' ? 'outline' : 'default'
  const visiblePrimaryVariant = publishEntitlementBlocked ? 'outline' : primaryVariant
  const hasLifecycleDropdown = dropdown.length > 0 || showDelete
  const PrimaryIcon = lifecycleIcon(primary, status)
  const PrimaryActionIcon = publishEntitlementBlocked ? LockKeyhole : PrimaryIcon
  const primaryTitle = publishEntitlementBlocked
    ? buildGatedAriaLabel(
        publishGate.featureLabel,
        publishGate.planName,
        PUBLISH_JOB_FEATURE_KEY,
      )
    : (publishBlockedTitle ?? primaryLabel)
  const publicUrl = jobDetailUrl.trim()
  const showShareMenu = Boolean(
    publicUrl && linkedinShareEnabled() && onShareLinkedIn,
  )

  const primaryButton = (
    <Button
      type="button"
      variant={visiblePrimaryVariant}
      size="sm"
      disabled={busy || !token}
      title={primaryTitle}
      aria-label={primaryTitle}
      splitLead={hasLifecycleDropdown}
      onClick={handlePrimaryClick}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <PrimaryActionIcon
          className={publishEntitlementBlocked ? 'h-4 w-4 text-promotion' : 'h-4 w-4'}
          aria-hidden
        />
      )}
      {primaryLabel}
    </Button>
  )

  const lifecycleMenuItems = (includePrimary: boolean) => (
    <>
      {includePrimary ? (
        <DropdownMenuItem
          className="gap-2"
          disabled={busy || !token}
          onSelect={(event) => {
            if (publishEntitlementBlocked) {
              event.preventDefault()
              setPublishGateDialogOpen(true)
              return
            }
            if (publishBlocked) {
              event.preventDefault()
              handleFixPublishGaps()
              return
            }
            handleAction(primary, false)
          }}
        >
          <PrimaryActionIcon
            className={publishEntitlementBlocked ? 'h-4 w-4 text-promotion' : 'h-4 w-4'}
            aria-hidden
          />
          {primaryLabel}
        </DropdownMenuItem>
      ) : null}
      {dropdown.map((action) => {
        const Icon = lifecycleIcon(action, status)
        return (
          <DropdownMenuItem
            key={action}
            className="gap-2"
            disabled={busy || !token}
            onClick={() => handleAction(action, lifecycleActionNeedsConfirm(action))}
          >
            <Icon className="h-4 w-4" aria-hidden />
            {LIFECYCLE_ACTION_LABELS[action]}
          </DropdownMenuItem>
        )
      })}
      {showDelete ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="gap-2 text-destructive focus:text-destructive"
            disabled={busy || !token}
            onClick={() => setPendingDelete(true)}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  )

  const lifecyclePrimary = (
    <div className="inline-flex items-stretch">
      {publishEntitlementBlocked ? (
        <GatePeek
          allowed={false}
          featureKey={PUBLISH_JOB_FEATURE_KEY}
          featureLabel={publishGate.featureLabel}
          planName={publishGate.planName}
          upgradePath={publishGate.upgradePath}
        >
          {primaryButton}
        </GatePeek>
      ) : publishBlocked ? (
        <PublishReadinessPeek blockers={publishBlockerItems} onFixDetails={handleFixPublishGaps}>
          {primaryButton}
        </PublishReadinessPeek>
      ) : (
        primaryButton
      )}
      {hasLifecycleDropdown ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant={visiblePrimaryVariant}
              size="sm"
              disabled={busy || !token}
              splitChevron
              aria-label="More job status actions"
            >
              <ButtonDropdownChevron />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">{lifecycleMenuItems(false)}</DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  )

  return (
    <>
      <div className="hidden @min-[480px]:block">
        <PageHeaderActions
          tertiary={
            <>
              {publicUrl ? <ViewPublicLinkButton url={publicUrl} iconOnly /> : null}
              <JobDuplicateButton
                opportunityId={opportunity.id}
                token={token}
                queryClient={queryClient}
                onDuplicated={onDuplicated}
              />
              {publicUrl && !showShareMenu ? (
                <CopyPublicLinkButton url={publicUrl} iconOnly disabled={busy} />
              ) : null}
            </>
          }
          secondary={
            showShareMenu ? (
              <PublicShareMenu
                url={publicUrl}
                onShareLinkedIn={onShareLinkedIn}
                disabled={busy}
              />
            ) : null
          }
          primary={lifecyclePrimary}
        />
      </div>

      <div className="@min-[480px]:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghostToolbar"
              size="iconSm"
              disabled={busy || !token}
              aria-label="Job actions"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {publicUrl ? (
              <>
                <DropdownMenuItem asChild>
                  <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="gap-2">
                    <ExternalLink className="h-4 w-4" aria-hidden />
                    View public page
                  </a>
                </DropdownMenuItem>
              </>
            ) : null}
            <DropdownMenuItem
              className="gap-2"
              disabled={busy || !token}
              onClick={() => void runDuplicate()}
            >
              <Copy className="h-4 w-4" aria-hidden />
              Duplicate job
            </DropdownMenuItem>
            {publicUrl ? (
              <>
                <PublicShareMenuItems
                  url={publicUrl}
                  onShareLinkedIn={showShareMenu ? onShareLinkedIn : undefined}
                  disabled={busy}
                />
                <DropdownMenuSeparator />
              </>
            ) : (
              <DropdownMenuSeparator />
            )}
            {lifecycleMenuItems(true)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={publishGateDialogOpen} onOpenChange={setPublishGateDialogOpen}>
        <DialogContent className="sm:max-w-md border-promotion/30">
          <DialogHeader>
            <DialogTitle>
              {gatingSurfaceTitle(PUBLISH_JOB_FEATURE_KEY, publishGate.featureLabel)}
            </DialogTitle>
          </DialogHeader>
          <GatePeekPanel
            featureKey={PUBLISH_JOB_FEATURE_KEY}
            featureLabel={publishGate.featureLabel}
            planName={publishGate.planName}
            upgradePath={publishGate.upgradePath}
            onUpgrade={handlePublishUpgrade}
          />
        </DialogContent>
      </Dialog>

      <JobWorkspaceActionDialogs
        busy={busy}
        pendingConfirm={pendingConfirm}
        onPendingConfirmChange={(open) => {
          if (!open) setPendingConfirm(null)
        }}
        onConfirmAction={() => {
          if (pendingConfirm) void runAction(pendingConfirm)
        }}
        pendingDelete={pendingDelete}
        onPendingDeleteChange={(open) => {
          if (!open) setPendingDelete(false)
        }}
        onConfirmDelete={() => void runDelete()}
      />
    </>
  )
}
